/**
 * CarePlanTab — orchestrator for the Care Plans tab.
 *
 * Phase 3 (Care Plan v2) extraction from ParticipantDetailPage.js. Owns data
 * fetch + actions and composes CarePlanList, CarePlanForm, CarePlanDetail,
 * and (when v2 flag is ON) the template gallery, acknowledgement dialog and
 * version diff viewer.
 *
 * When `care_plan_v2_enabled` is OFF the surface is byte-equivalent to the
 * pre-Phase-3 inline tab.
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, FilePlus2, Grid3X3 } from 'lucide-react';
import { CarePlanList } from './CarePlanList';
import { CarePlanForm } from './CarePlanForm';
import { CarePlanDetail } from './CarePlanDetail';
import { CarePlanAcknowledgementDialog } from './CarePlanAcknowledgementDialog';
import { StructuredCarePlanEditor } from './structured/StructuredCarePlanEditor';
import { StructuredEditorErrorBoundary } from './structured/StructuredEditorErrorBoundary';
import { CoordinatorCommentModeration } from './CoordinatorCommentModeration';
import { CreateCarePlanDialog, STAFF_VISIBILITY_NOTE } from './CreateCarePlanDialog';
import { useAuth } from '@/lib/auth';

const v2Enabled = () => {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.care_plan_v2_enabled;
  } catch { return false; }
};

const templateMgmtEnabled = () => {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.participant_careplan_template_management_enabled;
  } catch { return false; }
};

const structuredMatrixEnabled = () => {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.care_plan_v2_structured_matrix_enabled;
  } catch { return false; }
};

const domainsEnabled = () => {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.participant_careplan_domains_enabled;
  } catch { return false; }
};

// Known Care Plan v2 Structured Matrix domain types — MUST match
// backend/routes/careplan_domains.py DOMAIN_CATALOG keys. Used to validate
// the ?domain= URL hint from the Care & Support landing dashboard.
const KNOWN_DOMAINS = new Set([
  'personal_care',
  'daily_living',
  'nutrition',
  'community_access',
  'transport',
  'assistive_equipment',
]);

const EMPTY_FORM = {
  title: '', medical_alerts: '', personal_care_needs: '',
  dietary_requirements: '', routine_morning: '', important_notes: '',
  review_due_date: '',
};

export function CarePlanTab({ participantId, currentUser }) {
  const [plans, setPlans] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [detailPlan, setDetailPlan] = useState(null);
  const [ackDialog, setAckDialog] = useState({ open: false, plan: null });
  const [diff, setDiff] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [structuredPlanId, setStructuredPlanId] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const structuredRef = useRef(null);
  const isV2 = v2Enabled();
  const isStructured = structuredMatrixEnabled();
  const isDomains = domainsEnabled();
  const isTemplateMgmt = templateMgmtEnabled();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('careplans.create');
  const canEdit = hasPermission('careplans.update');

  // Care & Support landing deep-link (Correction 2 — deterministic selection).
  //
  // Consume URL hints from the landing card:
  //   • ?domain=<domain_type>  — validated against KNOWN_DOMAINS; unknown → null
  //   • ?planId=<care_plan_id> — an EXPLICIT plan id takes highest precedence
  //
  // Deterministic plan-selection policy (per management directive):
  //   1. Explicit ?planId= if it matches a non-archived plan the user can access
  //   2. If user has careplans.update: single non-archived plan (APPROVED, REVIEW
  //      or DRAFT) → open it
  //   3. Read-only user: single readable non-archived plan (APPROVED or REVIEW
  //      only; drafts are not readable content for RO users) → open it
  //   4. Multiple candidates → DO NOT auto-open; leave the Care Plans list
  //      visible so the user can choose explicitly (existing selection UI)
  //   5. Zero candidates → safe no-data state (existing empty-state)
  //
  // Guardrails: unknown ?domain= silently ignored; auto-open runs at most once
  // per (requestedDomain, requestedPlanId) pair to prevent reopen loops after
  // the user closes the drawer; no plan is auto-created; approved plans are
  // never mutated; Template Selection / versioning / lifecycle / permissions
  // are never bypassed (auto-open only ATTACHES to an existing plan record).
  const [searchParams] = useSearchParams();
  const requestedDomain = useMemo(() => {
    const d = searchParams.get('domain');
    return d && KNOWN_DOMAINS.has(d) ? d : null;
  }, [searchParams]);
  const requestedPlanId = useMemo(() => {
    const p = searchParams.get('planId');
    // Only accept non-empty, printable string ids; ignore obvious garbage.
    return p && typeof p === 'string' && p.length > 0 && p.length < 100 ? p : null;
  }, [searchParams]);
  const autoOpenKeyRef = useRef(null);

  useEffect(() => {
    // Skip when nothing requested, when core flags OFF, or when no plans yet.
    if (!requestedDomain) return;
    if (!(isStructured && isDomains)) return;
    if (!plans || plans.length === 0) return;
    if (structuredPlanId) return;
    // Compute an idempotency key for this (domain, explicit-plan) request.
    const key = `${requestedDomain}::${requestedPlanId || '_any_'}`;
    if (autoOpenKeyRef.current === key) return;  // already attempted this exact request

    // Priority 1: explicit ?planId= that exists AND is not archived.
    if (requestedPlanId) {
      const explicit = plans.find(
        (p) => String(p.id) === String(requestedPlanId)
              && String(p.status || '').toUpperCase() !== 'ARCHIVED',
      );
      if (explicit) {
        autoOpenKeyRef.current = key;
        setStructuredPlanId(explicit.id);
        return;
      }
      // Invalid planId → mark attempted, fall through to no-data state.
      autoOpenKeyRef.current = key;
      return;
    }

    // Priority 2/3: single-candidate deterministic pick.
    const isEditor = canEdit || canCreate;
    const nonArchived = plans.filter(
      (p) => String(p.status || '').toUpperCase() !== 'ARCHIVED',
    );
    const editableStatuses = new Set(['APPROVED', 'REVIEW', 'DRAFT']);
    const readableStatuses = new Set(['APPROVED', 'REVIEW']);
    const eligible = nonArchived.filter((p) => {
      const s = String(p.status || '').toUpperCase();
      return isEditor ? editableStatuses.has(s) : readableStatuses.has(s);
    });

    if (eligible.length === 0) {
      // Priority 5: zero candidates → mark attempted, show empty state.
      autoOpenKeyRef.current = key;
      return;
    }

    if (eligible.length === 1) {
      // Priority 2/3: exactly one candidate → deterministic open.
      autoOpenKeyRef.current = key;
      setStructuredPlanId(eligible[0].id);
      return;
    }

    // Priority 4: multiple candidates → DO NOT auto-open. User picks
    // explicitly via the existing Care Plans list UI. Mark attempted so
    // we don't loop, but preserve the current list-visible state.
    autoOpenKeyRef.current = key;
  }, [requestedDomain, requestedPlanId, isStructured, isDomains, plans, structuredPlanId, canEdit, canCreate]);

  // When the structured matrix opens, scroll it into view — it renders inline
  // below the plan list, so without this it can mount below the fold and look
  // like nothing happened.
  useEffect(() => {
    if (structuredPlanId && structuredRef.current) {
      structuredRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [structuredPlanId]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/careplans/by-participant/${participantId}?pageSize=50`);
      setPlans(r.data.items || r.data.data || []);
    } catch (e) { /* toast handled centrally */ }
  }, [participantId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!isV2) return;
    api.get('/careplans/templates').then(r => setTemplates(r.data.data || [])).catch(() => {});
  }, [isV2]);

  // BS3-012: say where a new plan stands (staff only see approved plans) and never fail silently
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/careplans', { participant_id: participantId, ...form });
      setForm(EMPTY_FORM);
      setCreateOpen(false);
      toast.success('Care plan created', { description: STAFF_VISIBILITY_NOTE });
      await refresh();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const createFromTemplate = async (tid) => {
    try {
      await api.post(`/careplans/from-template/${tid}`, { participant_id: participantId });
      setTemplateOpen(false);
      toast.success('Care plan created from template', { description: STAFF_VISIBILITY_NOTE });
      await refresh();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/careplans/${id}/${action}`);
      toast.success(action === 'approve' ? 'Care plan approved — staff can now see it'
        : action === 'submit-review' ? 'Submitted for review — staff will see it once approved' : 'Care plan updated');
      await refresh();
    } catch (err) {
      // e.g. readiness gate on submit, or HIGH-risk behaviour rows incomplete on approve
      toast.error(getErrorMessage(err) || (action === 'approve' ? 'Approval blocked' : 'Action failed'));
    }
  };

  const exportPdf = async (id) => {
    try {
      const r = await api.get(`/careplans/${id}/export-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'careplan.pdf'; a.click();
    } catch (e) { /* toast */ }
  };

  const openDiff = async (plan) => {
    if (!plan.previous_version_id) return;
    try {
      const r = await api.get(`/careplans/${plan.id}/diff/${plan.previous_version_id}`);
      setDiff({ plan, payload: r.data.data });
    } catch (e) { /* toast */ }
  };

  const submitAck = async (payload) => {
    if (!ackDialog.plan) return;
    try {
      await api.post(`/careplans/${ackDialog.plan.id}/acknowledge-signoff`, payload);
      await refresh();
      if (detailPlan && detailPlan.id === ackDialog.plan.id) {
        const r = await api.get(`/careplans/${detailPlan.id}`);
        setDetailPlan(r.data.data);
      }
    } catch (e) { /* toast */ }
  };

  return (
    <div className="space-y-4">
      <CreateCarePlanDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        participantId={participantId}
        existingPlans={plans}
        onCreated={async (plan) => {
          await refresh();
          if (isStructured && plan?.id) setStructuredPlanId(plan.id);
        }}
      />
      <div className="flex justify-between">
        <h3 className="font-bold">Care Plans</h3>
        <div className="flex gap-2">
          {canCreate && (isTemplateMgmt ? (
            <Button size="sm" onClick={() => setCreateDialogOpen(true)} data-testid="care-plan-new-button">
              <Plus className="h-3 w-3 mr-1" /> New Care Plan
            </Button>
          ) : (
            <>
              {isV2 && (
                <Button size="sm" variant="outline"
                        onClick={() => setTemplateOpen(true)}
                        data-testid="care-plan-template-button">
                  <FilePlus2 className="h-3 w-3 mr-1" /> From Template
                </Button>
              )}
              <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="care-plan-new-button">
                <Plus className="h-3 w-3 mr-1" /> New Plan
              </Button>
            </>
          ))}
        </div>
      </div>

      {createOpen && (
        <CarePlanForm
          value={form}
          onChange={setForm}
          onSubmit={create}
          onCancel={() => setCreateOpen(false)}
          v2Enabled={isV2}
          participantId={participantId}
        />
      )}

      {isV2 && templateOpen && (
        <Card data-testid="care-plan-template-gallery">
          <CardContent className="p-4 space-y-2">
            <h4 className="font-semibold text-sm">Care Plan Templates</h4>
            {templates.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No templates configured yet. Templates can be seeded via the admin / settings UI.
              </p>
            ) : (
              <ul className="space-y-1">
                {templates.map(t => (
                  <li key={t.id} className="flex items-center justify-between border rounded px-3 py-2">
                    <div>
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.description}</p>
                    </div>
                    <Button size="sm" variant="ghost"
                            onClick={() => createFromTemplate(t.id)}
                            data-testid={`care-plan-template-use-${t.id}`}>
                      Use
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-right">
              <Button size="sm" variant="ghost" onClick={() => setTemplateOpen(false)}>Close</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <CarePlanList
        plans={plans}
        onAction={doAction}
        onExportPdf={exportPdf}
        onOpenDetail={async (p) => {
          // Re-fetch the full doc so v2 linked entities + acks are present
          try {
            const r = await api.get(`/careplans/${p.id}`);
            setDetailPlan(r.data.data);
          } catch (e) { setDetailPlan(p); }
        }}
        onOpenAck={(p) => setAckDialog({ open: true, plan: p })}
        onOpenDiff={openDiff}
        v2Enabled={isV2}
        extraActions={isStructured ? [{
          label: 'Open Matrix',
          icon: <Grid3X3 className="h-3 w-3 mr-1" />,
          onClick: (p) => setStructuredPlanId(p.id),
          testId: 'care-plan-structured-open',
        }] : []}
      />

      {isStructured && structuredPlanId && (
        <div ref={structuredRef} data-testid="structured-careplan-anchor">
          <StructuredEditorErrorBoundary onClose={() => setStructuredPlanId(null)}>
            <StructuredCarePlanEditor
              planId={structuredPlanId}
              currentUser={currentUser}
              onClose={() => setStructuredPlanId(null)}
            />
          </StructuredEditorErrorBoundary>
        </div>
      )}

      {/* Phase 6.2 — Portal Comments moderation queue (coordinator+).
          Self-hides when `portal_comments_enabled` is OFF (the moderation
          endpoint 404s and the component returns null). */}
      <CoordinatorCommentModeration participantId={participantId} />

      {detailPlan && (
        <CarePlanDetail
          plan={detailPlan}
          onClose={() => setDetailPlan(null)}
          linkedGoals={detailPlan.linked_goals}
          linkedRisks={detailPlan.linked_risks}
          linkedRestrictivePractices={detailPlan.linked_restrictive_practices}
          linkedMedications={detailPlan.linked_medications}
          acknowledgements={detailPlan.acknowledgements}
        />
      )}

      {isV2 && (
        <CarePlanAcknowledgementDialog
          open={ackDialog.open}
          onOpenChange={(o) => setAckDialog(s => ({ ...s, open: o }))}
          plan={ackDialog.plan}
          onSubmit={submitAck}
        />
      )}

      {isV2 && diff && (
        <Card data-testid="care-plan-diff-viewer">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">
                Diff · {diff.plan.plan_no} v{diff.plan.version} ↔ v{(diff.plan.version || 2) - 1}
              </h4>
              <Button size="sm" variant="ghost" onClick={() => setDiff(null)}>Close</Button>
            </div>
            {(!diff.payload?.changes || diff.payload.changes.length === 0) ? (
              <p className="text-xs text-muted-foreground italic">No textual differences.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {diff.payload.changes.map((c, i) => (
                  <li key={i} className="border rounded px-3 py-2">
                    <div className="text-xs font-mono text-muted-foreground">{c.field}</div>
                    <div className="grid grid-cols-2 gap-2 mt-1 text-sm">
                      <div className="bg-red-50 border border-red-200 rounded px-2 py-1 whitespace-pre-wrap">
                        {c.before || <span className="italic text-muted-foreground">empty</span>}
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1 whitespace-pre-wrap">
                        {c.after || <span className="italic text-muted-foreground">empty</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
